import type { AslDefinition } from '@/types/workflow-graph';

export const EMPTY_ASL: AslDefinition = { StartAt: '', States: {} };

export function unwrapAslDefinition(raw: unknown): AslDefinition {
    if (!raw) return EMPTY_ASL;
    if (typeof raw === 'string') {
        try {
            return unwrapAslDefinition(JSON.parse(raw));
        } catch {
            return EMPTY_ASL;
        }
    }
    if (typeof raw !== 'object') return EMPTY_ASL;
    const obj = raw as Record<string, unknown>;

    if ('definition' in obj && obj.definition && typeof obj.definition === 'object') {
        const inner = obj.definition as Record<string, unknown>;
        if ('States' in inner || 'StartAt' in inner) {
            return inner as unknown as AslDefinition;
        }
    }

    if (
        'definition' in obj &&
        typeof obj.definition === 'string' &&
        obj.definition.length > 0
    ) {
        try {
            return unwrapAslDefinition(JSON.parse(obj.definition));
        } catch {
            return EMPTY_ASL;
        }
    }

    if ('workflow' in obj && obj.workflow && typeof obj.workflow === 'object') {
        const workflowObj = obj.workflow as Record<string, unknown>;
        if ('definition' in workflowObj) {
            return unwrapAslDefinition(workflowObj.definition);
        }
    }

    if ('States' in obj || 'StartAt' in obj) {
        return obj as unknown as AslDefinition;
    }

    return EMPTY_ASL;
}

interface RevisionFile {
    path?: string;
    content_type?: string;
    content_text?: string | null;
    contentText?: string | null;
}

export function extractDefinitionFromRevision(candidate: Record<string, unknown>): AslDefinition | null {
    const files = (candidate.files as RevisionFile[] | undefined) ?? [];
    for (const file of files) {
        const text = file.content_text ?? file.contentText;
        if (!text) continue;
        try {
            const normalizedText = text.replace(/^\uFEFF/, '').trim();
            const parsed = JSON.parse(normalizedText);
            const asl = unwrapAslDefinition(parsed);
            if (asl.States && Object.keys(asl.States).length > 0) return asl;
        } catch {
            // skip non-JSON or malformed file content
        }
    }
    if (candidate.type_config) {
        const asl = unwrapAslDefinition(candidate.type_config);
        if (asl.States && Object.keys(asl.States).length > 0) return asl;
    }
    if (candidate.definition) {
        const asl = unwrapAslDefinition(candidate.definition);
        if (asl.States && Object.keys(asl.States).length > 0) return asl;
    }
    return null;
}

export function extractDefinitionFromEntity(
    entity: Record<string, unknown>,
    revisionId?: string,
    workingCopyId = 'working-copy',
): AslDefinition {
    const workingRevision = entity.working_revision as Record<string, unknown> | null;
    const immutableRevisions = (entity.immutable_revisions as Record<string, unknown>[] | undefined) ?? [];

    if (revisionId && revisionId !== workingCopyId) {
        const target = immutableRevisions.find(
            (r) => (r as { id?: string }).id === revisionId,
        );
        if (target) {
            const asl = extractDefinitionFromRevision(target);
            if (asl) return asl;
        }
    }

    const latestImmutable = immutableRevisions.length > 0 ? immutableRevisions[0] : null;
    const candidates = [workingRevision, latestImmutable, entity].filter(Boolean) as Record<string, unknown>[];

    for (const candidate of candidates) {
        const asl = extractDefinitionFromRevision(candidate);
        if (asl) return asl;
    }

    return EMPTY_ASL;
}
